import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import type { Crumb } from "../utils/structuredData";
import Blueprint, { blueprintSlots } from "../components/catalog/Blueprint";
import QuestionStep from "../components/catalog/QuestionStep";
import Welcome, { BuildingQueue } from "../components/catalog/Welcome";
import QueueHub from "../components/catalog/QueueHub";
import QueueItem from "../components/catalog/QueueItem";
import YourPlan, { BrowseAll, NothingFits, ServiceSuggestions } from "../components/catalog/YourPlan";
import CatalogCheckout from "../components/catalog/CatalogCheckout";
import DetailsStep from "../components/catalog/DetailsStep";
import { StageBar, prefersReducedMotion } from "../components/catalog/ui";
import {
  type QueueEntry, type QuestionId, acknowledgement, answersForDashboard, detailsNote, fitFor, joinWords, questionList,
  stageOf,
} from "../components/catalog/assessment";
import { type CatalogSession, useCatalogSession } from "../hooks/useCatalogSession";
import {
  type Answers, type CatalogSuggestion, type CartItem, CatalogError, catalogApi, type CatalogSchema, type Listing,
  type Picks, type Setup,
} from "../services/catalogApi";

type Stage = "welcome" | "q" | "building" | "details" | "hub" | "item" | "plan" | "book" | "nofit" | "browse";

const crumbs: Crumb[] = [
  { name: "Home", path: "/" },
  { name: "Catalog", path: "/catalog" },
];
const MAX_QUEUE = 5;
const MAX_DETAILS = 1500;
const UNAVAILABLE = "We can't load the catalog right now. Please try again in a few minutes.";

// The title in the slim bar above each step. The welcome screen has its own hero; the item screen shows its position.
const TITLES: Record<Exclude<Stage, "welcome" | "item">, string> = {
  q: "Your assessment", building: "Your assessment", details: "Your queue", hub: "Your queue", plan: "Your plan",
  book: "Book a call to go over your plan", nofit: "Your queue", browse: "Everything in our catalog",
};

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

/**
 * The catalog: a short assessment that fills in the visitor's blueprint, then a queue of applications they step
 * through one at a time, then their plan and a call to go over it. Plain language throughout: no prices, plans or
 * technical details. Answers and the queue stay in this browser between visits.
 */
const Catalog: React.FC = () => {
  const { session, update, saved, restore, reset } = useCatalogSession();
  const { answers, details, done, setup, queue, decided } = session;
  const [schema, setSchema] = useState<CatalogSchema | null>(null);
  const [stage, setStage] = useState<Stage>("welcome");
  const [question, setQuestion] = useState<QuestionId>("problem");
  const [ack, setAck] = useState("");
  const [editing, setEditing] = useState(false);
  const [buildStep, setBuildStep] = useState(0);
  const [fanned, setFanned] = useState(true);
  const [position, setPosition] = useState(0);
  const [browse, setBrowse] = useState<Listing[]>([]);
  const [suggestions, setSuggestions] = useState<CatalogSuggestion[]>([]);
  // The dashboard's follow-up question when it could put something together with one more answer.
  const [followUp, setFollowUp] = useState<{ question: string; asked: number } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Which way the question or application card slides in.
  const [direction, setDirection] = useState<"next" | "back">("next");
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    catalogApi.schema().then(setSchema).catch(() => setNotice(UNAVAILABLE));
  }, []);

  useEffect(() => {
    const top = topRef.current?.getBoundingClientRect().top;
    if (top !== undefined && top < 0) window.scrollTo?.({ top: window.scrollY + top - 80 });
  }, [stage, question, position]);

  const fail = (error: unknown) => setNotice(error instanceof CatalogError ? error.message : UNAVAILABLE);
  const list = useMemo(() => questionList(answers, details), [answers, details]);
  const followUps = list.filter((q) => stageOf(q) === 0 && q !== "problem");

  /** The workspace answer decides where things run; a skipped one is treated as "several different apps". */
  const resolvePicks = useCallback(async (picks: Picks): Promise<Setup | null> => {
    if (!schema) return null;
    const workspace = schema.picks.find((p) => p.id === "workspace");
    const fallback = workspace?.options.find((o) => o.id === "several")?.id ?? workspace?.options[0]?.id;
    return catalogApi.resolve({ ...picks, workspace: picks.workspace ?? fallback });
  }, [schema]);

  const ask = (next: QuestionId, reaction: string) => {
    setDirection(stage === "q" && list.indexOf(next) < list.indexOf(question) ? "back" : "next");
    setQuestion(next);
    setAck(reaction);
    setStage("q");
  };

  const build = useCallback(async (from: CatalogSession, asked = 0) => {
    setStage("building");
    setBuildStep(0);
    const steps = prefersReducedMotion() ? 0 : 650;
    const animation = (async () => { for (let i = 1; i <= 4; i++) { await wait(steps); setBuildStep(i); } })();
    try {
      const resolved = from.setup ?? await resolvePicks(from.answers.picks);
      if (!resolved) throw new Error("No setup");
      const sent = answersForDashboard(from.answers, from.details);
      const [catalog, ranked] = await Promise.all([catalogApi.listings(resolved), catalogApi.match(resolved, sent, [])]);
      setSuggestions(ranked.suggestions);
      let question: string | null = null;
      const byId = new Map(catalog.listings.map((l) => [l.id, l]));
      let entries: QueueEntry[] = ranked.matches.slice(0, MAX_QUEUE).flatMap((m) => {
        const listing = byId.get(m.id);
        return listing ? [{ listing, reasons: m.reasons, fit: fitFor(m, false) }] : [];
      });
      if (!ranked.matched) {
        const composed = await catalogApi.compose(resolved, sent, sent.problems, asked);
        entries = composed.buildable
          ? [{ listing: composed.listing, reasons: [], fit: "Possible", token: composed.token }]
          : (sent.problems.length ? [] : entries);
        if (!composed.buildable && composed.needs_details && composed.question) question = composed.question;
      }
      await animation;
      const keep = Object.fromEntries(Object.entries(from.decided).filter(
        ([id, d]) => d === "added" && entries.some((e) => e.listing.id === id)));
      update({ setup: resolved, queue: entries, decided: keep, viewed: [], offered: ranked.offerings });
      setEditing(false);
      if (!entries.length && question) { setFollowUp({ question, asked }); setStage("details"); return; }
      if (!entries.length) { setStage("nofit"); return; }
      setFanned(prefersReducedMotion());
      setStage("hub");
      window.setTimeout(() => setFanned(true), 80);
    } catch (error) {
      await animation;
      fail(error);
      setStage("welcome");
    }
  }, [resolvePicks, update]);

  const advance = (from: QuestionId, next: CatalogSession, reaction: string) => {
    const order = questionList(next.answers, next.details);
    const after = order.slice(order.indexOf(from) + 1);
    const following = editing
      ? after.find((q) => !next.done.includes(q)) ?? order.find((q) => !next.done.includes(q))
      : after[0];
    if (following) ask(following, reaction);
    else void build(next);
  };

  const commit = (from: QuestionId, nextAnswers: Answers, nextDetails: typeof details, reaction?: string) => {
    const next: CatalogSession = { ...session, answers: nextAnswers, details: nextDetails,
                                   done: [...new Set([...done, from])] };
    update({ answers: nextAnswers, details: nextDetails, done: next.done });
    advance(from, next, reaction ?? acknowledgement(from, nextAnswers, nextDetails));
  };

  const change = (nextAnswers: Answers, nextDetails: typeof details, move?: boolean) => {
    update({ answers: nextAnswers, details: nextDetails });
    if (["workspace", "data", "payment"].includes(question) && nextAnswers.picks.workspace) {
      resolvePicks(nextAnswers.picks).then((resolved) => resolved && update({ setup: resolved })).catch(fail);
    }
    if (move) window.setTimeout(() => commit(question, nextAnswers, nextDetails), prefersReducedMotion() ? 0 : 260);
  };

  const start = () => {
    setEditing(false);
    ask(list.find((q) => !done.includes(q)) ?? "problem", "");
  };

  const jumpTo = (q: QuestionId) => {
    setEditing(true);
    ask(q, stage === "q" ? "" : "No problem. Change anything you like.");
  };

  const previous = () => {
    const i = list.indexOf(question);
    if (i <= 0) setStage(queue.length ? "hub" : "welcome");
    else ask(list[i - 1], "");
  };

  const openItem = (i: number) => {
    const id = queue[i]?.listing.id;
    if (!id) return;
    update((s) => ({ viewed: [...new Set([...s.viewed, id])] }));
    setDirection(stage === "item" && i < position ? "back" : "next");
    setPosition(i);
    setStage("item");
  };

  const next = () => (position + 1 < queue.length ? openItem(position + 1) : setStage("plan"));

  const openBrowse = async () => {
    setStage("browse");
    try {
      const resolved = setup ?? await resolvePicks({});
      if (resolved) setBrowse((await catalogApi.listings(resolved)).listings);
    } catch (error) {
      fail(error);
    }
  };

  if (!schema) {
    return (
      <div ref={topRef} className="relative flex min-h-[calc(100dvh-4rem)] flex-col pt-6 pb-6">
        <Seo title="Catalog | Contextral" description="Find the automations made for your business in about 3 minutes." />
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={crumbs} />
        </div>
        <section className="my-auto">
          <p className="py-16 text-center text-lg text-text-secondary">{notice ?? "Loading the catalog…"}</p>
        </section>
      </div>
    );
  }

  const slots = blueprintSlots(schema, answers, details, setup, done, followUps);
  const activeSlot = stage === "q" ? (stageOf(question) === 0 && question !== "problem" ? "shows" : question === "team" ? "business" : question) : "";
  const blueprint = (layout: "column" | "grid") => (
    <Blueprint slots={slots} layout={layout} active={activeSlot} onChange={jumpTo}
               canChange={(slot) => slot.filled || queue.length > 0} />
  );
  const added = queue.filter((e) => decided[e.listing.id] === "added");
  const allSkipped = queue.length > 0 && queue.every((e) => decided[e.listing.id] === "skipped");
  const problemWords = [...answers.problems, details.problemText].filter(Boolean).slice(0, 2);
  const workspaceLabel = schema.picks.find((p) => p.id === "workspace")?.options.find((o) => o.id === answers.picks.workspace)?.label;
  const hubLine = `${queue.length === 1 ? "One application" : `${queue.length} applications`} picked for ${
    answers.team_size ? `a team of ${answers.team_size.toLowerCase()}` : "your team"}${workspaceLabel ? ` on ${workspaceLabel}` : ""}${
    problemWords.length ? `, to help with ${joinWords(problemWords.map((p) => p.toLowerCase()))}` : ""}.`;
  const lead = { hub: hubLine, book: "We'll go over your selections, timing and pricing with you.",
                 browse: "Ready-made applications our team sets up for you." }[stage as string];
  const stageStep = stage === "q" ? stageOf(question) : ["building", "hub", "item"].includes(stage) ? 2 : null;
  const back = stage === "plan" ? { label: "Back to your queue", to: () => setStage("hub") }
    : stage === "book" ? { label: "Back to your plan", to: () => setStage("plan") } : null;
  const startOver = () => { reset(); setNotice(null); setStage("welcome"); };

  const resumeLine = saved && stage === "welcome"
    ? `You told us: ${[saved.answers.problems.join(", ") || saved.details.problemText,
                       saved.answers.tools.length ? `${saved.answers.tools.length} tools` : ""].filter(Boolean).join(" · ") || "a few answers"}. ${
       saved.queue.length ? "Your queue is waiting." : "Your queue is almost ready."}`
    : null;

  const services = session.services.flatMap((id) => schema.offerings.filter((o) => o.id === id));
  const toggleService = (id: string) => update((s) => ({
    services: s.services.includes(id) ? s.services.filter((x) => x !== id) : [...s.services, id] }));
  const serviceSuggestions = (
    <ServiceSuggestions offered={session.offered} cards={schema.offerings} added={session.services} onToggle={toggleService} />
  );
  const answerFollowUp = (answer: string) => {
    const nextAnswers = { ...answers, details: [answers.details, answer].filter(Boolean).join("\n").slice(0, MAX_DETAILS) };
    update({ answers: nextAnswers });
    void build({ ...session, answers: nextAnswers }, (followUp?.asked ?? 0) + 1);
  };

  const cart: CartItem[] = [
    ...added.map((e) => (e.token ? { token: e.token } : { listing_id: e.listing.id })),
    ...services.map((o) => ({ offering_id: o.id })),
  ];
  const summary = [
    detailsNote(details),
    ...queue.filter((e) => decided[e.listing.id] === "skipped")
      .map((e) => `Not for them: ${e.listing.name}${session.notes[e.listing.id] ? ` (${session.notes[e.listing.id]})` : ""}`),
  ].filter(Boolean).join(". ");

  return (
    <div ref={topRef} className="relative flex min-h-[calc(100dvh-4rem)] flex-col pt-6 pb-6">
      <Seo title="Catalog | Contextral" description="Find the automations made for your business in about 3 minutes, then book a short call to go over your plan." />
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <Breadcrumbs items={crumbs} />
      </div>
      <section className="my-auto">
        {stage !== "welcome" && (
          <div className="mb-8 flex flex-wrap items-end justify-between gap-x-6 gap-y-4 border-b border-borderSubtle pb-5">
            <div key={stage} className="catalog-enter flex min-w-0 flex-[1_1_420px] flex-col gap-1.5">
              {back && (
                <button type="button" onClick={back.to}
                        className="inline-flex items-center gap-1.5 self-start text-sm font-semibold text-primary hover:underline">
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />{back.label}
                </button>
              )}
              {stage === "item" ? (
                <h1 className="mb-0 flex flex-wrap items-baseline gap-2 font-display text-2xl font-bold text-text-primary sm:text-3xl">
                  <button type="button" onClick={() => setStage("hub")} className="text-primary hover:underline">Your queue</button>
                  <span aria-hidden="true" className="text-text-muted">›</span>
                  <span>{position + 1} of {queue.length}</span>
                </h1>
              ) : (
                <h1 className="mb-0 font-display text-2xl font-bold text-text-primary sm:text-3xl">{TITLES[stage]}</h1>
              )}
              {lead && <p className="text-text-secondary sm:text-lg">{lead}</p>}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {stageStep !== null && <StageBar current={stageStep} />}
              <button type="button" onClick={startOver} className="min-h-[44px] px-2 text-sm font-semibold text-text-muted hover:text-primary">
                Start over
              </button>
            </div>
          </div>
        )}
        {notice && <p role="alert" className="catalog-enter mx-auto mb-6 max-w-3xl rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{notice}</p>}

        <div key={stage} className="catalog-enter">
        {stage === "welcome" && (
          <Welcome resume={resumeLine} onStart={start} onBrowse={() => void openBrowse()}
                   onResume={() => {
                     if (!saved) return;
                     restore(saved);
                     if (saved.queue.length) setStage("hub");
                     else { setEditing(true); ask(questionList(saved.answers, saved.details).find((q) => !saved.done.includes(q)) ?? "problem",
                                                 "Welcome back. Here is where you left off."); }
                   }}
                   onStartOver={reset} />
        )}

        {stage === "q" && (
          <div className="flex flex-wrap items-start gap-8">
            <div key={question} className={`min-w-0 flex-[999_1_560px] catalog-${direction}`}>
              <QuestionStep key={question} question={question} schema={schema} answers={answers} details={details}
                            acknowledgement={ack} runs={setup?.explanation ?? null} onChange={change}
                            onContinue={() => commit(question, answers, details)}
                            onSkip={() => commit(question, answers, details, "No problem, we can skip that.")}
                            onBack={previous} />
            </div>
            <aside aria-label="Your blueprint" className="min-w-0 flex-[1_1_320px] lg:sticky lg:top-24">{blueprint("column")}</aside>
          </div>
        )}

        {stage === "building" && (
          <BuildingQueue step={buildStep} rows={[
            { label: "Your problem", value: problemWords.join(", ") || "Not given" },
            { label: "Your tools", value: answers.tools.length ? joinWords(answers.tools.slice(0, 4)) : "We'll pick them with you" },
            { label: "Where it will run", value: setup?.explanation ?? "We'll decide this with you" },
            { label: "Your queue", value: buildStep >= 4 ? "Applications found for you" : "Matching against our catalog" },
          ]} />
        )}

        {stage === "hub" && (
          <QueueHub queue={queue} decided={decided} viewed={session.viewed} fanned={fanned} answers={blueprint("grid")}
                    onStart={() => openItem(Math.max(0, queue.findIndex((e) => !decided[e.listing.id] && !session.viewed.includes(e.listing.id))))}
                    onPlan={() => setStage("plan")}
                    onRestart={() => { update({ decided: Object.fromEntries(added.map((e) => [e.listing.id, "added" as const])), viewed: [] }); openItem(0); }} />
        )}

        {stage === "item" && queue[position] && (
          <div key={position} className={`catalog-${direction}`}>
          <QueueItem entry={queue[position]} position={position} total={queue.length}
                     added={decided[queue[position].listing.id] === "added"} explanation={setup?.explanation ?? null}
                     answers={answers} details={details} tools={schema?.tools ?? []} offerings={schema.offerings}
                     onNext={next}
                     onToggleAdd={() => {
                       const id = queue[position].listing.id;
                       update((s) => {
                         const d = { ...s.decided };
                         if (d[id] === "added") delete d[id]; else d[id] = "added";
                         return { decided: d };
                       });
                     }}
                     onSkip={(reason) => {
                       const id = queue[position].listing.id;
                       update((s) => ({ decided: { ...s.decided, [id]: "skipped" }, notes: { ...s.notes, [id]: reason } }));
                       next();
                     }} />
          </div>
        )}

        {stage === "plan" && (
          <YourPlan items={added} services={services} runs={setup?.explanation ?? null} allSkipped={allSkipped}
                    onRemoveService={toggleService} extra={serviceSuggestions}
                    onBook={() => setStage("book")}
                    onRestart={() => { update({ decided: Object.fromEntries(added.map((e) => [e.listing.id, "added" as const])), viewed: [] }); openItem(0); }}
                    onRemove={(id) => update((s) => { const d = { ...s.decided }; delete d[id]; return { decided: d }; })} />
        )}

        {stage === "book" && (
          <div className="mx-auto max-w-3xl">
            <CatalogCheckout picks={answers.picks} answers={answersForDashboard(answers, details)} cart={cart} transcript={[]}
                             summary={summary} onBack={() => setStage("plan")} onBooked={() => update({ decided: {}, notes: {}, viewed: [] })} />
          </div>
        )}

        {stage === "nofit" && (
          <NothingFits said={details.problemText} suggestions={suggestions} onChange={() => jumpTo("problem")}
                       onBrowse={() => void openBrowse()} extra={serviceSuggestions}
                       onPlan={services.length ? () => setStage("plan") : undefined} />
        )}

        {stage === "details" && followUp && (
          <DetailsStep key={followUp.asked} question={followUp.question} onSubmit={answerFollowUp}
                       onSkip={() => setStage("nofit")} />
        )}

        {stage === "browse" && <BrowseAll listings={browse} offerings={schema.offerings} onStart={start} />}
        </div>
      </section>
    </div>
  );
};

export default Catalog;
